/**
 * Smart alignment guides — the red lines that appear while you drag.
 *
 * This is the difference between a canvas that feels like a drawing
 * program and one that feels like a toy. Without it, laying out a chain
 * of forty parties means nudging every box by hand and still ending up
 * with a chart whose columns are three pixels out; with it, a shape
 * clicks into line with its neighbours and stays there.
 *
 * TWO KINDS, and they are not the same idea:
 *
 *   · ALIGNMENT — an edge or a centre of the dragged shape lines up with
 *     an edge or centre of a stationary one. Six candidates per axis
 *     (left/centre/right, top/middle/bottom), because a chart is read
 *     off centres as often as off edges.
 *
 *   · SPACING — the gap either side of the dragged shape is equal. This
 *     is what makes a row of eight heirs sit evenly without anybody
 *     measuring, and it is the one most editors leave out.
 *
 * The dragged shape is never MOVED by this module. It returns an offset
 * and the lines to draw, and the caller applies both — so a snap is
 * always undoable as part of the drag it belongs to, and a guide can be
 * drawn without committing to it.
 */
import {
  bottom, boundsOf, centerX, centerY, right,
} from './geometry.js';

// How close, in SCREEN pixels, before a guide engages. Screen rather
// than world so the feel is the same at 16% and at 400% — a world-space
// threshold would make snapping impossible when zoomed out and
// inescapable when zoomed in.
export const SNAP_PIXELS = 6;

// How far outside a box its measure bar is drawn, in screen pixels.
export const MEASURE_CLEARANCE = 7;

// The step a shape turns in, in degrees. Upright, square, and the
// diagonals between — which is every angle a chain of title uses, and
// the reason a box at 3° reads as a mistake rather than as a decision.
export const ROTATE_STEP = 15;

// The dots on the canvas, in world units. Exported so the background and
// the snapping cannot drift apart: a grid you can see but not land on is
// worse than no grid at all.
export const GRID = 20;

/** The candidate lines a rect offers on each axis. */
function verticals(r) {
  return [
    { at: r.x, kind: 'left' },
    { at: centerX(r), kind: 'center' },
    { at: right(r), kind: 'right' },
  ];
}

function horizontals(r) {
  return [
    { at: r.y, kind: 'top' },
    { at: centerY(r), kind: 'middle' },
    { at: bottom(r), kind: 'bottom' },
  ];
}

/**
 * The best snap on one axis, plus every line that ties with it.
 *
 * Ties are kept rather than resolved because they are informative: a
 * shape whose left edge AND centre both line up is telling you two
 * different things about the layout, and drawing only the first hides
 * the second.
 */
function bestOn(movingLines, staticLines, tolerance) {
  let offset = 0;
  let best = Infinity;
  for (const m of movingLines) {
    for (const s of staticLines) {
      const delta = s.at - m.at;
      const distance = Math.abs(delta);
      if (distance < best - 1e-9) { best = distance; offset = delta; }
    }
  }
  if (best > tolerance) return { offset: 0, at: [] };
  // Everything that lands on the SAME resulting position, so the guides
  // drawn are exactly the ones the snap satisfied.
  const at = new Set();
  for (const m of movingLines) {
    for (const s of staticLines) {
      if (Math.abs((s.at - m.at) - offset) < 1e-6) at.add(s.at);
    }
  }
  return { offset, at: [...at] };
}

/**
 * Equal-gap guides.
 *
 * Looks only along the axis being tested and only at shapes that
 * actually overlap the dragged one on the other axis — otherwise a box
 * on the far side of a section chart "spaces" against one it has no
 * visual relationship to, and the guide is noise.
 */
function spacingOn(moving, others, axis, tolerance) {
  const lead = axis === 'x' ? 'x' : 'y';
  const size = axis === 'x' ? 'w' : 'h';
  const crossStart = axis === 'x' ? 'y' : 'x';
  const crossSize = axis === 'x' ? 'h' : 'w';

  const near = others.filter((o) => (
    o[crossStart] < moving[crossStart] + moving[crossSize]
    && o[crossStart] + o[crossSize] > moving[crossStart]
  )).sort((a, b) => a[lead] - b[lead]);
  if (near.length < 2) return { offset: 0, spans: [] };

  let bestOffset = 0;
  let bestDistance = Infinity;
  let bestSpans = [];

  for (let i = 0; i < near.length - 1; i += 1) {
    const a = near[i];
    const b = near[i + 1];
    const gap = b[lead] - (a[lead] + a[size]);
    if (gap <= 0) continue;

    // Between them, centred in the gap.
    const between = a[lead] + a[size] + (gap - moving[size]) / 2;
    // Outside, one gap further along in either direction.
    const afterB = b[lead] + b[size] + gap;
    const beforeA = a[lead] - gap - moving[size];

    for (const [target, spans] of [
      [between, [{ from: a[lead] + a[size], to: between }, { from: between + moving[size], to: b[lead] }]],
      [afterB, [{ from: a[lead] + a[size], to: b[lead] }, { from: b[lead] + b[size], to: afterB }]],
      [beforeA, [{ from: beforeA + moving[size], to: a[lead] }, { from: a[lead] + a[size], to: b[lead] }]],
    ]) {
      const distance = Math.abs(target - moving[lead]);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestOffset = target - moving[lead];
        bestSpans = spans;
      }
    }
  }

  if (bestDistance > tolerance) return { offset: 0, spans: [] };
  return { offset: bestOffset, spans: bestSpans };
}

/**
 * Where a dragged rect wants to land, and what to draw while it does.
 *
 * `moving` is the proposed rect (already offset by the drag), `others`
 * the rects it may snap to.
 *
 * THREE THINGS, IN ORDER, per axis. Alignment with a neighbour beats
 * equal spacing, which beats the grid: lining up with a particular box
 * is the strongest intent somebody can have, and the grid is what to do
 * when they have not expressed one. Offering more than one at a time
 * would have them fighting over the same pixel.
 *
 * The grid quantises the RESULT rather than the movement, so a box that
 * was three pixels off lands on the grid the first time it is moved —
 * which is the whole point. `grid: 0` turns it off; the canvas passes
 * that while alt is held, the same escape alignment has.
 */
export function snapRect(moving, others, scale = 1, { tolerance = SNAP_PIXELS, grid = GRID } = {}) {
  const world = tolerance / (scale || 1);
  const rest = others.filter(Boolean);

  const vertical = bestOn(verticals(moving), rest.flatMap(verticals), world);
  const horizontal = bestOn(horizontals(moving), rest.flatMap(horizontals), world);

  const spacingX = vertical.at.length ? { offset: 0, spans: [] } : spacingOn(moving, rest, 'x', world);
  const spacingY = horizontal.at.length ? { offset: 0, spans: [] } : spacingOn(moving, rest, 'y', world);

  const onGrid = (value) => (grid > 0 ? Math.round(value / grid) * grid - value : 0);

  const dx = vertical.at.length ? vertical.offset
    : spacingX.offset || onGrid(moving.x);
  const dy = horizontal.at.length ? horizontal.offset
    : spacingY.offset || onGrid(moving.y);
  const landed = { ...moving, x: moving.x + dx, y: moving.y + dy };

  // A guide is drawn the length of everything it touches, so the line
  // visibly connects the shape to what it aligned with rather than
  // floating across the whole canvas.
  const lines = [];
  for (const at of vertical.at) {
    const touching = rest.filter((r) => verticals(r).some((v) => Math.abs(v.at - at) < 1e-6));
    const span = boundsOf([...touching, landed]);
    lines.push({ axis: 'x', at, from: span.y, to: bottom(span) });
  }
  for (const at of horizontal.at) {
    const touching = rest.filter((r) => horizontals(r).some((h) => Math.abs(h.at - at) < 1e-6));
    const span = boundsOf([...touching, landed]);
    lines.push({ axis: 'y', at, from: span.x, to: right(span) });
  }

  return {
    dx,
    dy,
    rect: landed,
    lines,
    spacing: [
      ...spacingX.spans.map((s) => ({ axis: 'x', ...s, at: centerY(landed) })),
      ...spacingY.spans.map((s) => ({ axis: 'y', ...s, at: centerX(landed) })),
    ],
  };
}

// Where a label wants to sit on a line: the middle, or a quarter of the
// way along either side of it. Not a grid — a label's position is a
// FRACTION of a line that moves whenever its boxes move, and a chart is
// read off "the label in the middle of that arrow", not off coordinates.
export const LABEL_STOPS = Object.freeze([0, 0.25, 0.5, 0.75, 1]);

/**
 * A label's place along its line, pulled to the nearest usual one.
 *
 * TOLERANCE-BASED, unlike the grid, and that is the point: a label has
 * to be placeable anywhere, because the reason somebody drags one is
 * that where it belongs is under a line that crosses it. Quantising
 * would take away the only move that resolves the collision. This just
 * makes the ordinary positions easy to hit.
 *
 * Reach is measured in screen pixels ALONG THE LINE and converted to a
 * fraction, so it feels the same on a short arrow and a long one — a
 * fixed fraction would be four pixels on one and forty on the other.
 * The middle gets half again as much, because it is where a label
 * belongs unless there is a reason.
 */
export function snapAlong(t, length, scale = 1, { tolerance = SNAP_PIXELS, stops = LABEL_STOPS } = {}) {
  const raw = Number(t);
  const at = Math.max(0, Math.min(1, Number.isFinite(raw) ? raw : 0.5));
  if (!(length > 0) || !(tolerance > 0)) return at;

  const reach = (tolerance / (scale || 1)) / length;
  let best = at;
  let closest = Infinity;
  for (const stop of stops) {
    const distance = Math.abs(stop - at);
    const allowed = stop === 0.5 ? reach * 1.5 : reach;
    if (distance <= allowed && distance < closest) { closest = distance; best = stop; }
  }
  return best;
}

/**
 * A loose point on the grid.
 *
 * For the bends in a connector, which belong to no rect and so cannot go
 * through either of the two above. A bend three units off looks like a
 * bend three units off — on a chart of forty conveyances the dog-legs
 * are what the eye follows, and one out of true is visible from across
 * the room.
 *
 * `grid: 0` leaves the point where it is, the escape alt gives.
 */
export function snapPoint(point, grid = GRID) {
  if (!(grid > 0)) return { x: point.x, y: point.y };
  return {
    x: Math.round(point.x / grid) * grid,
    y: Math.round(point.y / grid) * grid,
  };
}

/**
 * Where a rect being RESIZED wants to land.
 *
 * Dragging a handle had no snapping at all, which is the reason a chain
 * of party boxes ends up with eleven boxes at 160 wide and one at 163.
 * Two things are worth catching, and they compete on one number because
 * both come down to where the moving edge ends up:
 *
 *   · A MATCHING SIZE — the box becomes exactly as wide (or as tall) as
 *     one already on the chart. This is the one that matters here. A
 *     chain is read as columns, and a column of boxes that are nearly
 *     the same width reads as a mistake even when nobody can say why.
 *
 *   · AN EDGE IN LINE — the moving edge lands on a neighbour's edge or
 *     centre, which is the same idea `snapRect` applies to a move.
 *
 * Only the edges the handle actually holds are moved: dragging the east
 * handle must never shift the west one, or the box walks across the
 * chart while being resized. And nothing here snaps a rect to a size it
 * cannot have — `minSize` is the caller's, applied after.
 */
export function snapResize(
  proposed, handle, others, scale = 1, { tolerance = SNAP_PIXELS, grid = GRID } = {},
) {
  const world = tolerance / (scale || 1);
  // How far OUTSIDE the box a measure bar sits, in screen pixels. On the
  // edge itself it lands under the outline and the selection frame, and
  // a bar nobody can see is a bar that is not there.
  const clear = MEASURE_CLEARANCE / (scale || 1);
  const rest = others.filter(Boolean);
  const out = { ...proposed };
  const lines = [];
  const sizes = [];

  const holds = {
    west: handle.includes('w'),
    east: handle.includes('e'),
    north: handle.startsWith('n'),
    south: handle.startsWith('s'),
  };

  for (const axis of ['x', 'y']) {
    const horizontal = axis === 'x';
    const lead = horizontal ? 'x' : 'y';
    const size = horizontal ? 'w' : 'h';
    const low = horizontal ? holds.west : holds.north;
    const high = horizontal ? holds.east : holds.south;
    if (!low && !high) continue;

    // The edge under the pointer, and the one staying put.
    const movingAt = low ? out[lead] : out[lead] + out[size];
    const anchored = low ? out[lead] + out[size] : out[lead];

    const candidates = [];

    // Somebody else's edges and centres.
    for (const other of rest) {
      for (const line of (horizontal ? verticals(other) : horizontals(other))) {
        candidates.push({ at: line.at, kind: 'edge' });
      }
    }
    // Somebody else's exact width or height, as a position for this edge.
    const seen = new Set();
    for (const other of rest) {
      const span = other[size];
      if (!(span > 0) || seen.has(span)) continue;
      seen.add(span);
      candidates.push({
        at: low ? anchored - span : anchored + span,
        kind: 'size',
        span,
        of: other,
      });
    }

    // And the grid, when nothing on the chart applies: the SIZE becomes
    // a multiple of it.
    //
    // Not "the moving edge lands on a grid line", which sounds like the
    // same rule and is not. For a box square with the grid the two are
    // identical; for a box somebody deliberately put half a step out —
    // alt exists for exactly that — lining the edge up would drag it
    // back on to the grid and quietly undo the decision. Rounding the
    // size leaves it where it was put. One rule, so the result is the
    // same every time rather than turning on which landing happened to
    // be a pixel nearer.
    //
    // Never below one step: a box rounded to nothing cannot be grabbed
    // again.
    const gridSpan = grid > 0
      ? grid * Math.max(1, Math.round(Math.abs(movingAt - anchored) / grid))
      : null;
    const gridStop = gridSpan === null ? null : anchored + (low ? -gridSpan : gridSpan);

    let best = null;
    for (const candidate of candidates) {
      const distance = Math.abs(candidate.at - movingAt);
      if (distance > world) continue;
      // A matching size wins a tie: while resizing, "the same width as
      // that one" is what somebody is reaching for, and an edge that
      // happens to sit at the same place says less.
      if (!best || distance < best.distance - 1e-9
        || (Math.abs(distance - best.distance) < 1e-9 && candidate.kind === 'size' && best.kind === 'edge')) {
        best = { ...candidate, distance };
      }
    }

    // The grid is the fallback, never a competitor: a shape already on
    // the chart is a stronger thing to line up with than a line nobody
    // drew, exactly as it is for a move. Unconditional when it applies —
    // stops are one grid apart, so every drag lands on one.
    if (!best && gridStop !== null) {
      best = {
        at: gridStop, span: gridSpan, kind: 'grid', distance: Math.abs(gridStop - movingAt),
      };
    }
    if (!best) continue;

    // Taken from the span where there is one, rather than by subtracting
    // two positions: 763 - 603 is 160.00000000000003 in binary floating
    // point, and a box 160.00000000000003 wide is what then goes into
    // the document, the size field and the export.
    const span = best.span ?? Math.abs(best.at - anchored);
    if (low) {
      out[lead] = anchored - span;
      out[size] = span;
    } else {
      out[size] = span;
    }

    if (best.kind === 'grid') {
      // Nothing to draw. The dots are already on screen, and a guide
      // line along a grid line somebody can see is noise.
    } else if (best.kind === 'edge') {
      const touching = rest.filter((r) => (horizontal ? verticals(r) : horizontals(r))
        .some((line) => Math.abs(line.at - best.at) < 1e-6));
      const span = boundsOf([...touching, out]);
      lines.push(horizontal
        ? { axis: 'x', at: best.at, from: span.y, to: bottom(span) }
        : { axis: 'y', at: best.at, from: span.x, to: right(span) });
    } else {
      // Two measure bars: one under the box being sized, one under the
      // box it now matches. A guide line would be a lie here — nothing
      // is aligned; they are merely the same width.
      sizes.push({
        axis,
        span: best.span,
        bars: [
          {
            from: out[lead],
            to: out[lead] + out[size],
            at: (horizontal ? bottom(out) : right(out)) + clear,
          },
          {
            from: best.of[lead],
            to: best.of[lead] + best.of[size],
            at: (horizontal ? bottom(best.of) : right(best.of)) + clear,
          },
        ],
      });
    }
  }

  return { rect: out, lines, sizes };
}
